import React, { useContext, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { RadioGroup, RadioButton, Icon } from '@cbrebuild/blocks';
import Select from '../../../../nucleus/select/select';

import FileUploadAccordian from '../../file-upload-accordian';
import PropertyAutocomplete from '../../../../components/autocomplete/property-autocomplete';
import { search as esriSearch, buildingLookup, getEDPSuggestions } from '../../../../services/esri-service';
import { isUnitedStatesCountry, isValidZipCodeValue } from '../../validation';
import abbreviate from '../../../../utils/state-abbreviator';
import countries from '../../../../data/countries.json';
import states from '../../../../data/states.json';
import {
  splitPropertyTypeAndSubtype,
  groupPropertyTypesByCategory,
  getPropertyTypes,
  getSubtypesByType,
} from '../../../../utils/property-type-utils';

import '../../lease-abstraction.scss';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import propertiesService from '../../../../services/properties-service';

const getFilteredStates = (countryName = 'United States') => {
  const found = countries.find(c => c.display_name === countryName);
  return states.filter(s => s.country_id === found?.country_id);
};

const MIN_SEARCH_LENGTH = 1;
const DEBOUNCE_DELAY = 300;
const MAX_SUGGESTIONS = 15;
const ESRI_COUNTRY_CODES = 'USA,CAN,CL,MX,CO,AR,BR';

const normalizeWholeNumberSquareFootageValue = value => String(value || '')
  .split('.')[0]
  .replace(/\D/g, '')
  .slice(0, 12);

const normalizeParcelSizeValue = (value) => {
  const stringValue = String(value || '');
  const [wholeValue, decimalValue] = stringValue.split('.');
  if (!decimalValue || decimalValue.length <= 2) return stringValue;

  const normalizedDecimal = decimalValue.replace(/0+$/, '');
  return normalizedDecimal ? `${wholeValue}.${normalizedDecimal}` : wholeValue;
};

// ─── Individual field search hook ────────────────────────────────────────────
// Mirrors Angular's fieldSearchStates pattern: per-field debounced EDP→ESRI
// search triggered when user types in Address, City, or Zip fields.
const usePropertyFieldSearch = (getFieldValues, onSelectResult) => {
  const initState = () => ({ suggestions: [], showSuggestions: false, loading: false });
  const [fieldStates, setFieldStates] = useState({
    address: initState(),
    city: initState(),
    zip: initState(),
  });
  const timeouts = useRef({ address: null, city: null, zip: null });
  const canceled = useRef({ address: false, city: false, zip: false });

  const setField = useCallback((fieldName, patch) => {
    setFieldStates(prev => ({
      ...prev,
      [fieldName]: { ...prev[fieldName], ...patch },
    }));
  }, []);

  const composeSearchText = useCallback((activeField) => {
    const { address = '', city = '', state = '', zip = '' } = getFieldValues();
    switch (activeField) {
      case 'address': return address.trim();
      case 'city': return [address, city].filter(Boolean).join(' ').trim();
      case 'zip': return [address, city, state, zip].filter(Boolean).join(' ').trim();
      default: return address.trim();
    }
  }, [getFieldValues]);

  const performSearch = useCallback((fieldName, searchText) => {
    canceled.current[fieldName] = false;
    setField(fieldName, { loading: true, showSuggestions: false });

    const esriParams = { text: searchText, countryCode: ESRI_COUNTRY_CODES, f: 'json' };

    const showResults = (candidates) => {
      if (canceled.current[fieldName]) return;
      setField(fieldName, { suggestions: candidates, showSuggestions: candidates.length > 0, loading: false });
    };

    const fallbackEsri = () => {
      esriSearch(esriParams)
        .then((res) => {
          if (canceled.current[fieldName]) return;
          const suggs = (res && res.suggestions) || [];
          setField(fieldName, { suggestions: suggs, showSuggestions: suggs.length > 0, loading: false });
        })
        .catch(() => setField(fieldName, { suggestions: [], showSuggestions: false, loading: false }));
    };

    getEDPSuggestions({ search: searchText })
      .then((edpRes) => {
        if (canceled.current[fieldName]) return;
        const rawCandidates = edpRes && edpRes.results && edpRes.results.candidates;
        if (rawCandidates && rawCandidates.length > 0) {
          let candidates = rawCandidates.map(c => ({ ...c, text: c.address, address: undefined }));
          if (candidates.length >= MAX_SUGGESTIONS) {
            showResults(candidates.slice(0, MAX_SUGGESTIONS));
            return;
          }
          // Top up with ESRI
          esriSearch(esriParams)
            .then((esriRes) => {
              if (canceled.current[fieldName]) return;
              const suggs = (esriRes && esriRes.suggestions) || [];
              const unique = suggs.filter(s => !candidates.some(c => c.text === s.text));
              const combined = candidates.concat(unique.slice(0, MAX_SUGGESTIONS - candidates.length));
              showResults(combined);
            })
            .catch(() => showResults(candidates));
        } else {
          fallbackEsri();
        }
      })
      .catch(() => {
        if (!canceled.current[fieldName]) fallbackEsri();
      });
  }, [setField]);

  const triggerSearch = useCallback((fieldName) => {
    const searchText = composeSearchText(fieldName);
    if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
      setField(fieldName, { suggestions: [], showSuggestions: false });
      return;
    }
    // Cancel any in-flight search
    canceled.current[fieldName] = true;
    if (timeouts.current[fieldName]) clearTimeout(timeouts.current[fieldName]);
    timeouts.current[fieldName] = setTimeout(() => {
      timeouts.current[fieldName] = null;
      performSearch(fieldName, searchText);
    }, DEBOUNCE_DELAY);
  }, [composeSearchText, setField, performSearch]);

  const selectSuggestion = useCallback((fieldName, suggestion) => {
    // Close all field dropdowns
    setFieldStates(prev => ({
      address: { ...prev.address, showSuggestions: false },
      city: { ...prev.city, showSuggestions: false },
      zip: { ...prev.zip, showSuggestions: false },
    }));

    // ESRI suggestion — needs buildingLookup
    if (suggestion.magicKey) {
      buildingLookup({ singleLine: suggestion.text, magicKey: suggestion.magicKey, f: 'json' })
        .then((res) => {
          const c = res && res.candidates && res.candidates[0];
          if (!c) return;
          const attrs = c.attributes || {};
          const rawCountry = attrs.Country || '';
          const countryObj = countries.find(ct => ct.country_id_A3 === rawCountry)
            || countries.find(ct => ct.display_name === rawCountry);
          const stateAbbr = attrs.RegionAbbr || abbreviate(attrs.Region || '') || '';
          onSelectResult({
            name: attrs.PlaceName || attrs.StAddr || '',
            building: {
              address: attrs.StAddr || '',
              city: attrs.City || '',
              state_abbreviation: stateAbbr,
              country: countryObj ? countryObj.display_name : 'United States',
              postal_code: attrs.Country === 'CAN'
                ? `${attrs.Postal || ''} ${attrs.PostalExt || ''}`.trim()
                : (attrs.Postal || ''),
            },
            edp_property_id: c.edp_property_id || null,
          });
        })
        .catch(() => {});
    } else if (suggestion.attributes) {
      // EDP suggestion — attributes already present
      const attrs = suggestion.attributes;
      const rawCountry = attrs.Country || '';
      const countryObj = countries.find(ct => ct.country_id_A3 === rawCountry)
        || countries.find(ct => ct.display_name === rawCountry);
      const stateAbbr = attrs.RegionAbbr || abbreviate(attrs.Region || '') || '';
      onSelectResult({
        name: attrs.PlaceName || attrs.StAddr || '',
        building: {
          address: attrs.StAddr || '',
          city: attrs.City || '',
          state_abbreviation: stateAbbr,
          country: countryObj ? countryObj.display_name : 'United States',
          postal_code: attrs.Country === 'CAN'
            ? `${attrs.Postal || ''} ${attrs.PostalExt || ''}`.trim()
            : (attrs.Postal || ''),
        },
        edp_property_id: suggestion.edp_property_id || null,
      });
    }
  }, [onSelectResult]);

  const hideAll = useCallback(() => {
    setFieldStates(prev => ({
      address: { ...prev.address, showSuggestions: false },
      city: { ...prev.city, showSuggestions: false },
      zip: { ...prev.zip, showSuggestions: false },
    }));
  }, []);

  return { fieldStates, triggerSearch, selectSuggestion, hideAll };
};

const LeaseInformation = ({ forceExpanded, isLoaded, onExpand, propertyOptions, storageKey }) => {
  const {
    isEnhancedFlow,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    deal,
    updateDeal,
    properties,
    onPropertyChange,
    onPropertyBlur,
    onSpaceChange,
    onSpaceBlur,
    onCurrencyBlur,
    selectPropertyFromSearch,
    voucher,
    updateVoucher,
    agreementTypeData,
    transactionTypeData,
    spaceTypeData,
    buildingClassData,
  } = useContext(LeaseAbstractionContext);
  const cp = properties && properties.length > 0 ? properties[0] : null;
  const isEarlyInvoice = !!voucher?.is_early_invoice;
  const showRequiredValidation = showPageValidation && !isEarlyInvoice;
  const isPropertyListedByCBRE = cp && (
    cp.is_listed === true ||
    modalFieldState.isListedByCBRE.value === true ||
    modalFieldState.isListedByCBRE.value === 'true'
  );
  const isAgileDeal = deal && (deal.is_agile_deal === true || deal.is_agile_deal === 'true');
  const isUnitedStatesProperty = isUnitedStatesCountry(modalFieldState.country.value);
  const postalLabel = isUnitedStatesProperty ? 'Zip' : 'Postal Code';
  const dealNumberOfSeats = deal?.number_of_seats ?? '';
  const dealCostPerSeat = deal?.cost_per_seat ?? '';
  const [agileDealFields, setAgileDealFields] = useState({
    numberOfSeats: dealNumberOfSeats,
    costPerSeat: dealCostPerSeat,
  });

  useEffect(() => {
    setAgileDealFields({
      numberOfSeats: dealNumberOfSeats,
      costPerSeat: dealCostPerSeat,
    });
  }, [dealNumberOfSeats, dealCostPerSeat]);

  const updateAgileDealField = (fieldName, value) => {
    const patchValue = value === '' ? null : value;
    const currentValue = deal && deal[fieldName];
    const currentDisplayValue = currentValue ?? '';

    if (deal && deal.id && `${currentDisplayValue}` !== `${patchValue ?? ''}`) {
      updateDeal(deal.id, { [fieldName]: patchValue });
    }
  };

  // Fetch existing deal properties from /v2/landlord/properties?deal_id=... so they
  // can be shown as selectable cards above the search box (enhanced flow only).
  const [dealProperties, setDealProperties] = useState([]);
  useEffect(() => {
    if (!isLoaded || !isEnhancedFlow || !deal?.id) return;
    propertiesService.fetchDealProperties(deal.id)
      .then(res => setDealProperties((res && res.results) || []))
      .catch(() => setDealProperties([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, isLoaded]);

  // When the user clicks an existing deal-property card, map it into the format
  // that selectPropertyFromSearch expects and run the same population logic.
  const handleDealPropertySelect = (dp) => {
    selectPropertyFromSearch({
      name: dp.name || '',
      building: {
        address: dp.building.address || '',
        city: dp.building.city || '',
        postal_code: dp.building.postal_code || '',
        state_abbreviation: dp.building.state_abbreviation || '',
        country: dp.building.country || 'United States',
      },
      edp_property_id: dp.edp_property_id || null,
    });
  };

  // ─── Individual field search (address / city / zip) ────────────────────────
  const getFieldValues = useCallback(() => ({
    address: modalFieldState.address?.value || '',
    city: modalFieldState.city?.value || '',
    state: modalFieldState.stateName?.value || '',
    zip: modalFieldState.zip?.value || '',
  }), [modalFieldState.address, modalFieldState.city, modalFieldState.stateName, modalFieldState.zip]);

  const { fieldStates, triggerSearch: rawTriggerSearch, selectSuggestion, hideAll } = usePropertyFieldSearch(
    getFieldValues,
    selectPropertyFromSearch,
  );

  // Property search (address/city/zip autocomplete dropdowns) is part of the new
  // enhanced split-pane flow only. In the legacy modal flow we short-circuit
  // triggerSearch so no API calls fire and no suggestions UI is rendered.
  const triggerSearch = isEnhancedFlow ? rawTriggerSearch : () => {};

  // Close all field dropdowns on outside click. Use capture phase so we still
  // run if a child handler stops propagation in the bubble phase.
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.property-field-search-wrapper')) hideAll();
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [hideAll]);

  const renderFieldDropdown = (fieldName) => {
    if (!isEnhancedFlow) return null;
    const state = fieldStates[fieldName];
    if (!state.showSuggestions && !state.loading) return null;
    return (
      <div className="property-field-suggestions">
        {state.loading && <div className="property-field-suggestions__loading">Searching...</div>}
        {state.showSuggestions && state.suggestions.map((s, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className="property-field-suggestions__item"
            role="button"
            tabIndex={0}
            onMouseDown={(e) => { e.preventDefault(); selectSuggestion(fieldName, s); }}
            onKeyDown={(e) => { if (e.key === 'Enter') selectSuggestion(fieldName, s); }}
          >
            {s.text}
          </div>
        ))}
      </div>
    );
  };

  // Group property options by category and create dropdown options
  const { groupedPropertyData, propertyTypeOptions, subtypeOptions } = useMemo(() => {
    const grouped = groupPropertyTypesByCategory(propertyOptions);
    const types = getPropertyTypes(grouped);
    
    // Get selected property type from current value
    const fullValue = modalFieldState.propertyType.value || '';
    const { type: selectedType } = splitPropertyTypeAndSubtype(fullValue);
    
    // Get subtypes for currently selected type - get ALL subtypes, not filtered
    const subtypes = selectedType ? getSubtypesByType(grouped, selectedType) : [];
    
    return {
      groupedPropertyData: grouped,
      propertyTypeOptions: types,
      subtypeOptions: subtypes,
    };
  }, [propertyOptions, modalFieldState.propertyType.value]);

  // Extract the display values
  const { propertyType, propertySubtype } = useMemo(() => {
    const fullValue = modalFieldState.propertyType.value || '';
    return {
      propertyType: splitPropertyTypeAndSubtype(fullValue).type,
      propertySubtype: splitPropertyTypeAndSubtype(fullValue).subtype,
    };
  }, [modalFieldState.propertyType.value]);

  // Helper function to get full value for subtype selection
  const getFullValueForSubtype = (selectedValue) => {
    if (selectedValue === 'None' || !selectedValue) {
      return propertyType ? `${propertyType} - None` : ' - None';
    }
    const subtypes = getSubtypesByType(groupedPropertyData, propertyType);
    const matchingOption = subtypes.find(opt => opt.value === selectedValue);
    return matchingOption ? matchingOption.fullValue : selectedValue;
  };

  // Helper function to get full value for property type selection
  const getFullValueForPropertyType = (selectedType) => {
    const subtypes = getSubtypesByType(groupedPropertyData, selectedType);
    return subtypes.length > 0 ? subtypes[0].fullValue : selectedType;
  };

  return (
    <>
      <FileUploadAccordian accordianTitle="Lease Information" forceExpanded={forceExpanded} onExpand={onExpand} storageKey={storageKey}>
      <LeaseAbstractionField
        key="leaseStartDate"
        fieldType={LeaseAbstractionFieldType.DATE}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.leaseStartDate,
          label: 'Lease Start Date',
          errorMessage: (!modalFieldState.leaseStartDate.value && showRequiredValidation) ? 'Lease Start Date is required' : fieldErrorMap.leaseStartDate,
          key: "leaseStartDate"
        }}
        fieldChangeHandler={(value) => {
          modalFieldChangeHandler('leaseStartDate', { value });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_from: value || null });
        }}
        fieldValidityChangeHandler={(value) => {
          modalFieldChangeHandler(
            'isLeaseStartDateValid',
            { isLeaseStartDateValid: value },
            'multiple',
          );
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'lease_info',
            child_key: modalFieldState.leaseStartDate.trackingID,
            stateVariable: 'leaseStartDate',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('leaseStartDate', { value: extractedVal });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_from: extractedVal || null });
        }}
      />
      <LeaseAbstractionField
        key="leaseEndDate"
        fieldType={LeaseAbstractionFieldType.DATE}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.leaseEndDate,
          label: 'Lease Expiration Date',
          errorMessage: (!modalFieldState.leaseEndDate.value && showRequiredValidation) ? 'Lease Expiration Date is required' : fieldErrorMap.leaseEndDate,
          key: "leaseEndDate"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('leaseEndDate', { value: event });
          if (voucher && voucher.id) updateVoucher(voucher.id, { lease_expiration_date: event || null });
        }}
        fieldValidityChangeHandler={(value) => {
          modalFieldChangeHandler(
            'isLeaseEndDateValid',
            { isLeaseEndDateValid: value },
            'multiple',
          );
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'lease_info',
            child_key: modalFieldState.leaseEndDate.trackingID,
            stateVariable: 'leaseEndDate',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('leaseEndDate', { value: extractedVal });
          if (voucher && voucher.id) updateVoucher(voucher.id, { lease_expiration_date: extractedVal || null });
        }}
      />
      <LeaseAbstractionField
        key="leaseExecutionDate"
        fieldType={LeaseAbstractionFieldType.DATE}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.leaseExecutionDate,
          label: 'Lease Execution Date',
          errorMessage: (!modalFieldState.leaseExecutionDate.value && showRequiredValidation)
            ? 'Lease Execution Date is required' : fieldErrorMap.leaseExecutionDate,
          key: "leaseExecutionDate"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('leaseExecutionDate', { value: event });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_execution: event || null });
        }}
        fieldValidityChangeHandler={(value) => {
          modalFieldChangeHandler(
            'isLeaseExecutionDateValid',
            { isLeaseExecutionDateValid: value },
            'multiple',
          );
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'lease_info',
            child_key: modalFieldState.leaseExecutionDate.trackingID,
            stateVariable: 'leaseExecutionDate',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('leaseExecutionDate', { value: extractedVal });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_execution: extractedVal || null });
        }}
      />
      <LeaseAbstractionField
        key="leaseOccupancyDate"
        fieldType={LeaseAbstractionFieldType.DATE}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: modalFieldState.leaseOccupancyDate,
          label: 'Lease Occupancy Date',
          errorMessage: (!modalFieldState.leaseOccupancyDate.value && showRequiredValidation)
            ? 'Lease Occupancy Date is required' : fieldErrorMap.leaseOccupancyDate,
          key: "leaseOccupancyDate"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('leaseOccupancyDate', { value: event });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_occupancy: event || null });
        }}
        fieldValidityChangeHandler={(value) => {
          modalFieldChangeHandler(
            'isLeaseOccupancyDateValid',
            { isLeaseOccupancyDateValid: value },
            'multiple',
          );
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'lease_info',
            child_key: modalFieldState.leaseOccupancyDate.trackingID,
            stateVariable: 'leaseOccupancyDate',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('leaseOccupancyDate', { value: extractedVal });
          if (deal && deal.id) updateDeal(deal.id, { date_lease_occupancy: extractedVal || null });
        }}
      />
      {/* Rate Type — deal.lease_agreement_type */}
      {deal && deal.id && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">{!isEarlyInvoice && '* '}Rate Type</label>
          <Select
            className={!deal.lease_agreement_type && showRequiredValidation ? 'acc-select-error' : ''}
            defaultOption={deal.lease_agreement_type || 'Select'}
            options={agreementTypeData}
            onChange={(e) => updateDeal(deal.id, { lease_agreement_type: e.value })}
          />
          {!deal.lease_agreement_type && showRequiredValidation && (
            <span className="acc-error-message">Rate Type is required</span>
          )}
        </div>
      )}
      {/* Lease Transaction Type — deal.lease_income_type */}
      {deal && deal.id && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">{!isEarlyInvoice && '* '}Lease Transaction Type</label>
          <Select
            className={!deal.lease_income_type && showRequiredValidation ? 'acc-select-error' : ''}
            defaultOption={deal.lease_income_type || 'Select'}
            options={transactionTypeData}
            onChange={(e) => updateDeal(deal.id, { lease_income_type: e.value })}
          />
          {!deal.lease_income_type && showRequiredValidation && (
            <span className="acc-error-message">Lease Transaction Type is required</span>
          )}
        </div>
      )}
      {/* Is this an agile/coworking deal? — deal.is_agile_deal */}
      {deal && deal.id && (
        <>
          <div className="file-modal-group-full">
            <p className="file-modal-label">Is this an agile/coworking deal?</p>
            <RadioGroup
              name="abstraction-agile-deal"
              selectedValue={`${deal.is_agile_deal}`}
              onChange={e => updateDeal(deal.id, { is_agile_deal: e.target.value })}
              orientation="vertical"
            >
              <RadioButton value="false">No</RadioButton>
              <RadioButton value="true">Yes</RadioButton>
            </RadioGroup>
          </div>
          {isAgileDeal && (
            <>
              <div className="file-modal-group-full">
                <label className="file-modal-label">Number of Seats</label>
                <input
                  type="number"
                  className="file-modal-input"
                  name="number-of-seats"
                  value={agileDealFields.numberOfSeats}
                  onChange={e => setAgileDealFields(current => ({ ...current, numberOfSeats: e.target.value }))}
                  onBlur={e => updateAgileDealField('number_of_seats', e.target.value)}
                />
              </div>
              <div className="file-modal-group-full">
                <label className="file-modal-label">Cost per Seat ($)</label>
                <input
                  type="number"
                  className="file-modal-input"
                  name="cost-per-seat"
                  value={agileDealFields.costPerSeat}
                  onChange={e => setAgileDealFields(current => ({ ...current, costPerSeat: e.target.value }))}
                  onBlur={e => updateAgileDealField('cost_per_seat', e.target.value)}
                />
              </div>
            </>
          )}
        </>
      )}
      <LeaseAbstractionField
        key="isASublease"
        fieldType={LeaseAbstractionFieldType.RADIO}
        fieldData={{
          data: modalFieldState.isASublease,
          label: 'Is this a sublease?',
          errorMessage: fieldErrorMap.isASublease,
          key: "isASublease",
          radioButtons: [
            {
              value: 'false',
              text: 'No',
            },
            {
              value: 'true',
              text: 'Yes',
            },
          ],
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('isASublease', { value: event.target.value });
          if (deal && deal.id) updateDeal(deal.id, { is_sublease: event.target.value });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.isASublease.trackingID,
            stateVariable: 'isASublease',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('isASublease', { value: extractedVal });
          if (deal && deal.id) updateDeal(deal.id, { is_sublease: extractedVal });
        }}
      />
      <p className="file-modal-sub-heading">Property Being Leased</p>
      {/* Existing deal properties — shown as selectable cards so the user can
          pre-populate all property fields with one click (enhanced flow only).
          Filter out any property already selected (matched by name). */}
      {isEnhancedFlow && dealProperties.filter(dp => dp.name !== (modalFieldState.propertyName?.value || '')).length > 0 && (
        <ul className="acc-deal-property-list">
          {dealProperties.filter(dp => dp.name !== (modalFieldState.propertyName?.value || '')).map(dp => (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              key={dp.id}
              className="acc-deal-property-card"
              onClick={() => handleDealPropertySelect(dp)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleDealPropertySelect(dp); }}
            >
              <div className="acc-deal-property-card__text">
                <h4>{dp.name}</h4>
                <p>{dp.building.full_address}</p>
              </div>
              <span className="acc-add-icon">
                <Icon iconName="plus-circle-outline" size="large" />
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* Search for Property — EDP/ESRI autocomplete that auto-fills all property fields
          (enhanced split-pane flow only; legacy modal does not have this). */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Search for Property</label>
          <PropertyAutocomplete
            onSelect={property => selectPropertyFromSearch(property)}
            placeholder="Search by address, city, or property name"
          />
        </div>
      )}
      <LeaseAbstractionField
        key="propertyName"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: modalFieldState.propertyName,
          label: 'Property Name',
          errorMessage: fieldErrorMap.propertyName,
          key: "propertyName"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('propertyName', {
            value: event.target.value,
          });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'name', value: event.target.value } });
        }}
        onBlur={() => {
          if (cp) onPropertyBlur(cp.id)({ target: { name: 'name', value: modalFieldState.propertyName.value } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.propertyName.trackingID,
            stateVariable: 'propertyName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('propertyName', { value: extractedVal });
          if (cp) {
            onPropertyChange(cp.id)({ target: { type: 'text', name: 'name', value: extractedVal } });
            onPropertyBlur(cp.id)({ target: { name: 'name', value: extractedVal } });
          }
        }}
      />
      <LeaseAbstractionField
        key="country"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.country,
          label: 'Country',
          errorMessage: (!modalFieldState.country.value && showPageValidation) ? 'Country is required' : fieldErrorMap.country,
          options: countries,
          key: "country"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler(
            'multipleFieldNames',
            {
              country: { ...modalFieldState.country, value: event.value },
              stateName: { ...modalFieldState.stateName, value: '' },
              filteredStatesForProperty: {
                value: getFilteredStates(event.value),
              },
            },
            'multiple',
          );
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'country', value: event.value } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.country.trackingID,
            stateVariable: 'country',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('multipleFieldNames', {
            country: { ...modalFieldState.country, value: extractedVal },
            stateName: { ...modalFieldState.stateName, value: '' },
            filteredStatesForProperty: { value: getFilteredStates(extractedVal) },
          }, 'multiple');
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'country', value: extractedVal } });
        }}
      />
      <div className="property-field-search-wrapper">
        <LeaseAbstractionField
          key="address"
          fieldType={LeaseAbstractionFieldType.TEXT}
          isRequired
          fieldData={{
            data: modalFieldState.address,
            label: 'Address',
            errorMessage: (!modalFieldState.address.value && showPageValidation) ? 'Address is required' : fieldErrorMap.address,
            key: "address"
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('address', { value: event.target.value });
            if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'address', value: event.target.value } });
            triggerSearch('address');
          }}
          onFieldFocus={() => triggerSearch('address')}
          onBlur={() => {
            if (cp) onPropertyBlur(cp.id)({ target: { name: 'address', value: modalFieldState.address.value } });
            // Hide suggestion dropdown when focus leaves. Suggestion onMouseDown
            // calls preventDefault, so clicking a suggestion does not blur the
            // input and this scheduling never runs in that path.
            setTimeout(hideAll, 150);
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'lease_info',
              child_key: modalFieldState.address.trackingID,
              stateVariable: 'address',
            });
          }}
          acceptFieldChangeHandler={(extractedVal) => {
            modalFieldChangeHandler('address', { value: extractedVal });
            if (cp) {
              onPropertyChange(cp.id)({ target: { type: 'text', name: 'address', value: extractedVal } });
              onPropertyBlur(cp.id)({ target: { name: 'address', value: extractedVal } });
            }
          }}
        />
        {renderFieldDropdown('address')}
      </div>
      <div className="property-field-search-wrapper">
        <LeaseAbstractionField
          key="city"
          fieldType={LeaseAbstractionFieldType.TEXT}
          isRequired
          fieldData={{
            data: modalFieldState.city,
            label: 'City',
            errorMessage: (!modalFieldState.city.value && showPageValidation) ? 'City is required' : fieldErrorMap.city,
            key: "city"
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('city', { value: event.target.value });
            if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'city', value: event.target.value } });
            triggerSearch('city');
          }}
          onFieldFocus={() => triggerSearch('city')}
          onBlur={() => {
            if (cp) onPropertyBlur(cp.id)({ target: { name: 'city', value: modalFieldState.city.value } });
            setTimeout(hideAll, 150);
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'lease_info',
              child_key: modalFieldState.city.trackingID,
              stateVariable: 'city',
            });
          }}
          acceptFieldChangeHandler={(extractedVal) => {
            modalFieldChangeHandler('city', { value: extractedVal });
            if (cp) {
              onPropertyChange(cp.id)({ target: { type: 'text', name: 'city', value: extractedVal } });
              onPropertyBlur(cp.id)({ target: { name: 'city', value: extractedVal } });
            }
          }}
        />
        {renderFieldDropdown('city')}
      </div>
      <LeaseAbstractionField
        key="stateName"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.stateName,
          label: 'State',
          errorMessage: (!modalFieldState.stateName.value && showPageValidation) ? 'State is required' : fieldErrorMap.stateName,
          options: modalFieldState.filteredStatesForProperty.value,
          key: "stateName"
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('stateName', { value: event.value });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'state_abbreviation', value: event.value } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.stateName.trackingID,
            stateVariable: 'stateName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('stateName', { value: extractedVal });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'state_abbreviation', value: extractedVal } });
        }}
      />
      <div className="property-field-search-wrapper">
        <LeaseAbstractionField
          key="zip"
          fieldType={LeaseAbstractionFieldType.TEXT}
          isRequired={!isEarlyInvoice && isUnitedStatesProperty}
          fieldData={{
            data: modalFieldState.zip,
            label: postalLabel,
            errorMessage: (!modalFieldState.zip.value && showRequiredValidation && isUnitedStatesProperty)
              ? `${postalLabel} is required`
              : fieldErrorMap.zip,
            key: 'zip',
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('zip', { value: event.target.value });
            if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'postal_code', value: event.target.value } });
            triggerSearch('zip');
          }}
          onFieldFocus={() => triggerSearch('zip')}
          validateLeaseAbstractionFields={validateLeaseAbstractionFields}
          onBlur={(event) => {
            const zipValue = event.target.value;
            if (cp && (!isUnitedStatesProperty || isValidZipCodeValue(zipValue))) {
              onPropertyBlur(cp.id)({ target: { name: 'postal_code', value: zipValue } });
            }
            setTimeout(hideAll, 150);
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'lease_info',
              child_key: modalFieldState.zip.trackingID,
              stateVariable: 'zip',
            });
          }}
          acceptFieldChangeHandler={(extractedVal) => {
            modalFieldChangeHandler('zip', { value: extractedVal });
            if (cp) {
              onPropertyChange(cp.id)({ target: { type: 'text', name: 'postal_code', value: extractedVal } });
              onPropertyBlur(cp.id)({ target: { name: 'postal_code', value: extractedVal } });
            }
          }}
        />
        {renderFieldDropdown('zip')}
      </div>
      {/* Is Property Confidential? — cp.is_property_confidential */}
      {cp && (
        <div className="file-modal-group-full">
          <p className="file-modal-label">Is Property Confidential?</p>
          <RadioGroup
            name="abstraction-is-property-confidential"
            selectedValue={`${cp.is_property_confidential}`}
            onChange={(e) => onPropertyChange(cp.id)({ target: { type: 'radio', name: 'is_property_confidential', value: e.target.value } })}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
        </div>
      )}
      <LeaseAbstractionField
        key="isWholeBuilding"
        fieldType={LeaseAbstractionFieldType.RADIO}
        fieldData={{
          data: modalFieldState.isWholeBuilding,
          label: 'Is this the whole building, or suites in a building?',
          errorMessage: fieldErrorMap.isWholeBuilding,
          key: "isWholeBuilding",
          radioButtons: [
            {
              value: 'true',
              text: 'Whole Building',
            },
            {
              value: 'false',
              text: 'Suite(s)',
            },
          ],
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('isWholeBuilding', {
            value: event.target.value,
          });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'whole_building', value: event.target.value } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.isWholeBuilding.trackingID,
            stateVariable: 'isWholeBuilding',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('isWholeBuilding', { value: extractedVal });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'whole_building', value: extractedVal } });
        }}
      />
      <LeaseAbstractionField
        key="propertyType"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired={!isEarlyInvoice}
        fieldData={{
          data: {
            ...modalFieldState.propertyType,
            value: propertyType,
            chipDisplayValue: modalFieldState.propertyType.value || '',
          },
          label: 'Property Type',
          errorMessage: (!propertyType && showRequiredValidation) ? 'Property Type is required' : fieldErrorMap.propertyType,
          key: "propertyType",
          options: [{ value: '', display_name: 'None' }, ...propertyTypeOptions],
          isClearable: true,
        }}
        fieldChangeHandler={(event) => {
          // If empty value (None option), clear the field and patch to backend
          if (!event.value) {
            modalFieldChangeHandler('propertyType', { value: '' });
            if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'voucher_property_type_category', value: '' } });
            return;
          }
          // When property type is selected, construct the full value with the first subtype
          const fullValue = getFullValueForPropertyType(event.value);
          modalFieldChangeHandler('propertyType', { value: fullValue });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'voucher_property_type_category', value: fullValue } });
        }}
        individualFieldChangeHandler={(event) => {
          const fullValue = getFullValueForPropertyType(event.value);
          
          individualModalFieldChangeHandler({
            child_value: fullValue,
            parent_key: 'lease_info',
            child_key: modalFieldState.propertyType.trackingID,
            stateVariable: 'propertyType',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          const fullValue = getFullValueForPropertyType(extractedVal);
          modalFieldChangeHandler('propertyType', { value: fullValue });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'voucher_property_type_category', value: fullValue } });
        }}
      />
      <LeaseAbstractionField
        key="propertySubtype"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired={!!propertyType && !isEarlyInvoice}
        disabled={!propertyType}
        fieldData={{
          data: {
            ...modalFieldState.propertyType,
            value: propertySubtype === 'None' ? '' : propertySubtype || '',
            chipDisplayValue: modalFieldState.propertyType.value || '',
          },
          label: 'Property Subtype',
          errorMessage: (propertyType && (!propertySubtype || propertySubtype === 'None') && showRequiredValidation) ? 'Property Subtype is required' : fieldErrorMap.propertyType,
          key: "propertySubtype",
          placeholder: 'Select',
          options: subtypeOptions.filter(opt => opt.value !== 'None'),
          isClearable: true,
        }}
        fieldChangeHandler={(event) => {
          const fullValue = getFullValueForSubtype(event.value);
          modalFieldChangeHandler('propertyType', { value: fullValue });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'voucher_property_type_category', value: fullValue } });
        }}
        individualFieldChangeHandler={(event) => {
          const fullValue = getFullValueForSubtype(event.value);
          individualModalFieldChangeHandler({
            child_value: fullValue,
            parent_key: 'lease_info',
            child_key: modalFieldState.propertyType.trackingID,
            stateVariable: 'propertyType',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          const fullValue = getFullValueForSubtype(extractedVal);
          modalFieldChangeHandler('propertyType', { value: fullValue });
          if (cp) onPropertyChange(cp.id)({ target: { type: 'select', name: 'voucher_property_type_category', value: fullValue } });
        }}
      />
      {/* Space Type — cp.space_type */}
      {cp && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">{!isEarlyInvoice && '* '}Space Type</label>
          <Select
            className={!cp.space_type && showRequiredValidation ? 'acc-select-error' : ''}
            defaultOption={cp.space_type || 'Select'}
            options={spaceTypeData}
            onChange={(e) => onPropertyChange(cp.id)({ target: { type: 'select', name: 'space_type', value: e.value === null ? '' : e.value } })}
          />
          {!cp.space_type && showRequiredValidation && (
            <span className="acc-error-message">Space Type is required</span>
          )}
        </div>
      )}
      {(modalFieldState.isWholeBuilding.value?.toString() ?? '').toLowerCase() ===
        'true' && (
        <>
          <LeaseAbstractionField
            key="squareFeet"
            fieldType={LeaseAbstractionFieldType.TEXT}
            isRequired={!isEarlyInvoice}
            fieldData={{
              data: {
                ...modalFieldState.squareFeet,
                value: normalizeWholeNumberSquareFootageValue(modalFieldState.squareFeet.value),
              },
              label: 'Square Footage',
              errorMessage: (!modalFieldState.squareFeet.value && showRequiredValidation) ? 'Square Footage is required' : fieldErrorMap.squareFeet,
              key: "squareFeet"
            }}
            fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d{0,12}$/;
              if (regex.test(newValue)) {
                modalFieldChangeHandler('squareFeet', { value: newValue });
                if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'building_total_square_feet', value: newValue } });
              }
            }}
            validateLeaseAbstractionFields={validateLeaseAbstractionFields}
            onBlur={() => {
              if (cp) onPropertyBlur(cp.id)({ target: { name: 'building_total_square_feet', value: normalizeWholeNumberSquareFootageValue(modalFieldState.squareFeet.value) } });
            }}
            acceptFieldChangeHandler={(extractedVal) => {
              const normalized = normalizeWholeNumberSquareFootageValue(extractedVal);
              modalFieldChangeHandler('squareFeet', { value: normalized }, 'single');
              if (cp) {
                onPropertyChange(cp.id)({ target: { type: 'text', name: 'building_total_square_feet', value: normalized } });
                onPropertyBlur(cp.id)({ target: { name: 'building_total_square_feet', value: normalized } });
              }
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'lease_info',
                child_key: modalFieldState.squareFeet.trackingID,
                stateVariable: 'squareFeet',
              });
            }}
          />
        </>
      )}
      <LeaseAbstractionField
        key="parcelSize"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: {
            ...modalFieldState.parcelSize,
            value: normalizeParcelSizeValue(modalFieldState.parcelSize.value),
          },
          label: 'Parcel Size/Acres',
          errorMessage: fieldErrorMap.parcelSize,
          key: "parcelSize"
        }}
        fieldChangeHandler={(event) => {
          const newValue = event.target.value;
          const regex = /^\d+(\.\d{0,2})?$|^$/;
          if (regex.test(newValue) || newValue === '') {
            modalFieldChangeHandler('parcelSize', { value: newValue });
            if (cp) onPropertyChange(cp.id)({ target: { type: 'text', name: 'parcel_size', value: newValue } });
          }
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
        onBlur={() => {
          if (cp) onPropertyBlur(cp.id)({ target: { name: 'parcel_size', value: normalizeParcelSizeValue(modalFieldState.parcelSize.value) } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.parcelSize.trackingID,
            stateVariable: 'parcelSize',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          const regex = /^\d+(\.\d{0,2})?$/;
          const normalized = normalizeParcelSizeValue(extractedVal);
          if (regex.test(normalized) || normalized === '') {
            modalFieldChangeHandler('parcelSize', { value: normalized });
            if (cp) {
              onPropertyChange(cp.id)({ target: { type: 'text', name: 'parcel_size', value: normalized } });
              onPropertyBlur(cp.id)({ target: { name: 'parcel_size', value: normalized } });
            }
          }
        }}
      />
      <LeaseAbstractionField
        key="isListedByCBRE"
        fieldType={LeaseAbstractionFieldType.RADIO}
        fieldData={{
          data: modalFieldState.isListedByCBRE,
          label: 'Is the property listed by CBRE?',
          errorMessage: fieldErrorMap.isListedByCBRE,
          key: "isListedByCBRE",
          radioButtons: [
            {
              value: 'false',
              text: 'No',
            },
            {
              value: 'true',
              text: 'Yes',
            },
          ],
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('isListedByCBRE', {
            value: event.target.value,
          });
          if (cp) {
            onPropertyChange(cp.id)({ target: { type: 'select', name: 'is_listed', value: event.target.value } });
          }
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'lease_info',
            child_key: modalFieldState.isListedByCBRE.trackingID,
            stateVariable: 'isListedByCBRE',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('isListedByCBRE', { value: extractedVal });
          if (cp) {
            onPropertyChange(cp.id)({ target: { type: 'select', name: 'is_listed', value: extractedVal } });
          }
        }}
      />
      {isPropertyListedByCBRE && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">CBRE Listing #</label>
          <input
            type="text"
            className="file-modal-input"
            name="cbre_listing_number"
            value={cp.cbre_listing_number || ''}
            onChange={onPropertyChange(cp.id)}
            onBlur={onPropertyBlur(cp.id)}
          />
        </div>
      )}
      {/* Is Property Managed by CBRE? — cp.is_property_managed_by_cbre */}
      {cp && (
        <div className="file-modal-group-full">
          <p className="file-modal-label">Is Property Managed by CBRE?</p>
          <RadioGroup
            name="abstraction-is-managed-by-cbre"
            selectedValue={`${cp.is_property_managed_by_cbre}`}
            onChange={(e) => onPropertyChange(cp.id)({ target: { type: 'radio', name: 'is_property_managed_by_cbre', value: e.target.value } })}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
        </div>
      )}
      {/* Mix Property ID — cp.mix_property_id */}
      {cp && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Mix Property ID</label>
          <input
            type="text"
            className="file-modal-input"
            name="mix_property_id"
            value={cp.mix_property_id || ''}
            onChange={onPropertyChange(cp.id)}
            onBlur={onPropertyBlur(cp.id)}
          />
        </div>
      )}
      {/* Building Class — cp.building_class */}
      {cp && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Building Class</label>
          <Select
            defaultOption={cp.building_class || 'Select'}
            options={buildingClassData}
            onChange={(e) => onPropertyChange(cp.id)({ target: { type: 'select', name: 'building_class', value: e.value === null ? '' : e.value } })}
          />
        </div>
      )}
      {/* Portfolio Name — cp.portfolio_name */}
      {cp && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Portfolio Name</label>
          <input
            type="text"
            className="file-modal-input"
            name="portfolio_name"
            value={cp.portfolio_name || ''}
            onChange={onPropertyChange(cp.id)}
            onBlur={onPropertyBlur(cp.id)}
          />
        </div>
      )}
      {(modalFieldState.isWholeBuilding.value?.toString() ?? '').toLowerCase() ===
        'false' && (
        <>
          <p className="file-modal-sub-heading">Suite #1</p>
          <LeaseAbstractionField
            key="suite"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.suite,
              label: 'Suite',
              errorMessage: fieldErrorMap.suite,
              key: "suite"
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('suite', { value: event.target.value });
              if (cp) onSpaceChange(cp.id, 0)({ target: { name: 'address_line2', value: event.target.value } });
            }}
            onBlur={() => {
              if (cp) onSpaceBlur(cp.id, 0)({ target: { name: 'address_line2', value: modalFieldState.suite.value } });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'lease_info',
                child_key: modalFieldState.suite.trackingID,
                stateVariable: 'suite',
              });
            }}
            acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('suite', { value: extractedVal });
              if (cp) {
                onSpaceChange(cp.id, 0)({ target: { name: 'address_line2', value: extractedVal } });
                onSpaceBlur(cp.id, 0)({ target: { name: 'address_line2', value: extractedVal } });
              }
            }}
          />
          <LeaseAbstractionField
            key="floorChange"
            fieldType={LeaseAbstractionFieldType.TEXT}
            fieldData={{
              data: modalFieldState.floorChange,
              label: 'Floor',
              errorMessage: fieldErrorMap.floorChange,
              key: "floorChange"
            }}
            fieldChangeHandler={(event) => {
              modalFieldChangeHandler('floorChange', {
                value: event.target.value,
              });
              if (cp) onSpaceChange(cp.id, 0)({ target: { name: 'floor', value: event.target.value } });
            }}
            onBlur={() => {
              if (cp) onSpaceBlur(cp.id, 0)({ target: { name: 'floor', value: modalFieldState.floorChange.value } });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'lease_info',
                child_key: modalFieldState.floorChange.trackingID,
                stateVariable: 'floorChange',
              });
            }}
            acceptFieldChangeHandler={(extractedVal) => {
              modalFieldChangeHandler('floorChange', { value: extractedVal });
              if (cp) {
                onSpaceChange(cp.id, 0)({ target: { name: 'floor', value: extractedVal } });
                onSpaceBlur(cp.id, 0)({ target: { name: 'floor', value: extractedVal } });
              }
            }}
          />
          <LeaseAbstractionField
            key="squareFeet"
            fieldType={LeaseAbstractionFieldType.TEXT}
            isRequired={!isEarlyInvoice}
            fieldData={{
              data: modalFieldState.squareFeet,
              label: 'Square Feet',
              errorMessage: (!modalFieldState.squareFeet.value && showRequiredValidation) ? 'Square Feet is required' : fieldErrorMap.squareFeet,
              key: "squareFeet"
            }}
            fieldChangeHandler={(event) => {
              const newValue = event.target.value;
              const regex = /^\d+(\.\d{0,2})?$/;
              if (regex.test(newValue) || newValue === '') {
                modalFieldChangeHandler('squareFeet', { value: newValue });
                if (cp) onSpaceChange(cp.id, 0)({ target: { name: 'space_total_square_feet', value: newValue } });
              }
            }}
            validateLeaseAbstractionFields={validateLeaseAbstractionFields}
            onBlur={() => {
              if (cp) onSpaceBlur(cp.id, 0)({ target: { name: 'space_total_square_feet', value: modalFieldState.squareFeet.value } });
            }}
            individualFieldChangeHandler={(event) => {
              individualModalFieldChangeHandler({
                child_value: event.target.value,
                parent_key: 'lease_info',
                child_key: modalFieldState.squareFeet.trackingID,
                stateVariable: 'squareFeet',
              });
            }}
            acceptFieldChangeHandler={(extractedVal) => {
              const regex = /^\d+(\.\d{0,2})?$/;
              if (regex.test(extractedVal) || extractedVal === '') {
                modalFieldChangeHandler('squareFeet', { value: extractedVal });
                if (cp) {
                  onSpaceChange(cp.id, 0)({ target: { name: 'space_total_square_feet', value: extractedVal } });
                  onSpaceBlur(cp.id, 0)({ target: { name: 'space_total_square_feet', value: extractedVal } });
                }
              }
            }}
          />
        </>
      )}
    </FileUploadAccordian>
    </>
  );
};

LeaseInformation.propTypes = {
  forceExpanded: PropTypes.bool,
  isLoaded: PropTypes.bool,
  onExpand: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  propertyOptions: PropTypes.arrayOf(PropTypes.shape({
    display_name: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    value: PropTypes.any,
  })),
  storageKey: PropTypes.string,
};

LeaseInformation.defaultProps = {
  forceExpanded: false,
  isLoaded: false,
  onExpand: () => {},
  propertyOptions: [{ display_name: 'Select', value: null }],
  storageKey: '',
};

export default LeaseInformation;
